import { ProInstrumentId, normalizeProInstrumentId } from './proWidget';

export enum ProInstrument {
  GENERIC = 'GENERIC',
  ASES = 'ASES',
  OKS = 'OKS',
  OHS = 'OHS',
}

export interface AsesChoiceOption {
  value: number;
  label: string;
}

export interface AsesQuestion {
  id: string;
  text: string;
  type: 'short' | 'yesno' | 'scale' | 'choice';
  optional?: boolean;
  scored?: boolean;
  helpText?: string;
  min?: number;
  max?: number;
  scoreRole?: string;
  options?: AsesChoiceOption[];
}

export interface AsesSection {
  id: string;
  title: string;
  questions: AsesQuestion[];
}

export interface AsesInstrumentConfig {
  instrument: string;
  name: string;
  scoreMin: number;
  scoreMax: number;
  sections: AsesSection[];
}

export type ProWidgetLike = {
  widgetType?: string | null;
  label?: string | null;
  fineText?: string | null;
  labels?: string[] | null;
  proInstrument?: string | null;
  instrumentConfig?: AsesInstrumentConfig | null;
  start?: number | null;
  end?: number | null;
};

export const SCORED_OXFORD_QUESTION_IDS = [
  'q1',
  'q2',
  'q3',
  'q4',
  'q5',
  'q6',
  'q7',
  'q8',
  'q9',
  'q10',
  'q11',
  'q12',
] as const;

export const SCORED_OKS_QUESTION_IDS = SCORED_OXFORD_QUESTION_IDS;
export const SCORED_OHS_QUESTION_IDS = SCORED_OXFORD_QUESTION_IDS;

export const SCORED_ASES_QUESTION_IDS = [
  'q7',
  'q8',
  'q9',
  'q10',
  'q11',
  'q12',
  'q13',
  'q14',
  'q15',
  'q16',
  'q17',
] as const;

export function toProInstrumentEnum(instrumentId: ProInstrumentId): ProInstrument {
  if (instrumentId === 'ases_shoulder') {
    return ProInstrument.ASES;
  }
  if (instrumentId === 'oks_knee') {
    return ProInstrument.OKS;
  }
  if (instrumentId === 'ohs_hip') {
    return ProInstrument.OHS;
  }
  return ProInstrument.GENERIC;
}

export function resolveProInstrument(widget: ProWidgetLike): ProInstrument {
  if (widget.proInstrument === ProInstrument.ASES || widget.proInstrument === 'ASES') {
    return ProInstrument.ASES;
  }
  if (widget.proInstrument === ProInstrument.OHS || widget.proInstrument === 'OHS') {
    return ProInstrument.OHS;
  }
  if (widget.proInstrument === ProInstrument.OKS || widget.proInstrument === 'OKS') {
    return ProInstrument.OKS;
  }
  if (widget.proInstrument === ProInstrument.GENERIC || widget.proInstrument === 'GENERIC') {
    return ProInstrument.GENERIC;
  }
  if (widget.fineText?.toLowerCase() === 'ohs') {
    return ProInstrument.OHS;
  }
  if (widget.fineText?.toLowerCase() === 'oks') {
    return ProInstrument.OKS;
  }
  if (widget.fineText?.toLowerCase() === 'ases') {
    return ProInstrument.ASES;
  }
  const labelId = widget.labels?.[0];
  if (labelId === 'ohs_hip' || labelId === 'ohs') {
    return ProInstrument.OHS;
  }
  if (labelId === 'oks_knee' || labelId === 'oks') {
    return ProInstrument.OKS;
  }
  if (labelId === 'ases_shoulder' || labelId === 'ases') {
    return ProInstrument.ASES;
  }
  if (widget.label?.toLowerCase().includes('oxford hip')) {
    return ProInstrument.OHS;
  }
  if (widget.label?.toLowerCase().includes('oxford knee')) {
    return ProInstrument.OKS;
  }
  if (widget.label?.toLowerCase().includes('ases shoulder')) {
    return ProInstrument.ASES;
  }
  if (labelId === 'generic_0_10') {
    return ProInstrument.GENERIC;
  }
  return ProInstrument.GENERIC;
}

export function getInstrumentConfig(widget: ProWidgetLike): AsesInstrumentConfig | null {
  if (widget.instrumentConfig?.sections?.length) {
    return widget.instrumentConfig;
  }
  return null;
}

export function isOhsWidget(widget: ProWidgetLike): boolean {
  return resolveProInstrument(widget) === ProInstrument.OHS && !!getInstrumentConfig(widget);
}

export function shouldRenderOhsForm(widget: ProWidgetLike): boolean {
  return widget.widgetType === 'PRO' && isOhsWidget(widget);
}

export function isOksWidget(widget: ProWidgetLike): boolean {
  return resolveProInstrument(widget) === ProInstrument.OKS && !!getInstrumentConfig(widget);
}

export function shouldRenderOksForm(widget: ProWidgetLike): boolean {
  return widget.widgetType === 'PRO' && isOksWidget(widget);
}

export function isAsesWidget(widget: ProWidgetLike): boolean {
  return resolveProInstrument(widget) === ProInstrument.ASES && !!getInstrumentConfig(widget);
}

export function shouldRenderAsesForm(widget: ProWidgetLike): boolean {
  return widget.widgetType === 'PRO' && isAsesWidget(widget);
}

export function instrumentIdFromWidget(widget: ProWidgetLike): ProInstrumentId {
  if (resolveProInstrument(widget) === ProInstrument.OHS) {
    return 'ohs_hip';
  }
  if (resolveProInstrument(widget) === ProInstrument.OKS) {
    return 'oks_knee';
  }
  if (resolveProInstrument(widget) === ProInstrument.ASES) {
    return 'ases_shoulder';
  }
  if (widget.labels?.[0]) {
    return normalizeProInstrumentId(widget.labels[0]);
  }
  return 'generic_0_10';
}

export function getScaleBounds(question: AsesQuestion): { min: number; max: number } | null {
  const type = question.type?.toLowerCase();
  if (type === 'scale' || type === 'rating') {
    return { min: question.min ?? 0, max: question.max ?? 10 };
  }
  return null;
}

export function usesScaleInput(question: AsesQuestion): boolean {
  return getScaleBounds(question) !== null;
}

export function isOhsLeadWidget(widget: {
  label?: string | null;
  values?: string[] | null;
  content?: string | null;
}): boolean {
  if (widget.content?.toLowerCase().includes('ohs score')) {
    return true;
  }
  if (widget.label?.toLowerCase().includes('oxford hip')) {
    return true;
  }
  return false;
}

export function formatOhsLeadWidgetValue(widget: {
  values?: string[] | null;
  content?: string | null;
}): string {
  if (widget.content?.match(/OHS score/i)) {
    return widget.content;
  }
  const score = widget.values?.[0];
  if (score && /^\d+$/.test(score) && parseInt(score, 10) <= 48) {
    return `OHS score ${score}/48`;
  }
  return score ? `${score}/48` : 'No Response';
}

export function isOksLeadWidget(widget: {
  label?: string | null;
  values?: string[] | null;
  content?: string | null;
}): boolean {
  if (widget.content?.toLowerCase().includes('oks score')) {
    return true;
  }
  if (widget.label?.toLowerCase().includes('oxford knee')) {
    return true;
  }
  return false;
}

export function formatOksLeadWidgetValue(widget: {
  values?: string[] | null;
  content?: string | null;
}): string {
  if (widget.content?.match(/OKS score/i)) {
    return widget.content;
  }
  const score = widget.values?.[0];
  if (score && /^\d+$/.test(score) && parseInt(score, 10) <= 48) {
    return `OKS score ${score}/48`;
  }
  return score ? `${score}/48` : 'No Response';
}

export function isAsesLeadWidget(widget: {
  label?: string | null;
  values?: string[] | null;
  content?: string | null;
}): boolean {
  if (widget.content?.toLowerCase().includes('ases score')) {
    return true;
  }
  if (widget.label?.toLowerCase().includes('ases')) {
    return true;
  }
  return false;
}

export function formatAsesLeadWidgetValue(widget: {
  values?: string[] | null;
  content?: string | null;
}): string {
  if (widget.content?.match(/ASES score/i)) {
    return widget.content;
  }
  const score = widget.values?.[0];
  if (score && /^\d+$/.test(score) && parseInt(score, 10) <= 100) {
    return `ASES score ${score}/100`;
  }
  return score ? `${score}/100` : 'No Response';
}

export type ProReportScoreRow = {
  score?: number | null;
  scoreMax?: number | null;
  proInstrument?: string | null;
  values?: string[] | null;
  content?: string | null;
  label?: string | null;
};

/** Resolve instrument for a proReport row (field, content, or label heuristics). */
export function resolveProReportInstrument(row: ProReportScoreRow): ProInstrument {
  if (row.proInstrument === ProInstrument.ASES || row.proInstrument === 'ASES') {
    return ProInstrument.ASES;
  }
  if (row.proInstrument === ProInstrument.OHS || row.proInstrument === 'OHS') {
    return ProInstrument.OHS;
  }
  if (row.proInstrument === ProInstrument.OKS || row.proInstrument === 'OKS') {
    return ProInstrument.OKS;
  }
  if (row.proInstrument === ProInstrument.GENERIC || row.proInstrument === 'GENERIC') {
    return ProInstrument.GENERIC;
  }
  if (row.content?.match(/OHS score/i) || row.label?.toLowerCase().includes('oxford hip')) {
    return ProInstrument.OHS;
  }
  if (row.content?.match(/OKS score/i) || row.label?.toLowerCase().includes('oxford knee')) {
    return ProInstrument.OKS;
  }
  if (row.content?.match(/ASES score/i) || row.label?.toLowerCase().includes('ases')) {
    return ProInstrument.ASES;
  }
  return ProInstrument.GENERIC;
}

export function getProInstrumentScoreMax(instrument: ProInstrument): number {
  if (instrument === ProInstrument.ASES) {
    return 100;
  }
  if (instrument === ProInstrument.OHS || instrument === ProInstrument.OKS) {
    return 48;
  }
  return 10;
}

export function getProInstrumentJointLabel(instrument: ProInstrument): string {
  if (instrument === ProInstrument.ASES) {
    return 'Shoulder';
  }
  if (instrument === ProInstrument.OHS) {
    return 'Hip';
  }
  if (instrument === ProInstrument.OKS) {
    return 'Knee';
  }
  return 'Other';
}

/** Parse numeric score from a proReport row for averaging (null if unscored). */
export function parseProReportNumericScore(row: ProReportScoreRow): number | null {
  if (row.score != null && !Number.isNaN(row.score)) {
    return row.score;
  }

  const contentMatchers = [
    /OHS score\s+(\d+)\s*\/\s*48/i,
    /OKS score\s+(\d+)\s*\/\s*48/i,
    /ASES score\s+(\d+)\s*\/\s*100/i,
  ];
  for (const matcher of contentMatchers) {
    const match = row.content?.match(matcher);
    if (match) {
      return parseInt(match[1], 10);
    }
  }

  const first = row.values?.[0];
  if (first && /^\d+$/.test(first) && !first.startsWith('{')) {
    return parseInt(first, 10);
  }

  return null;
}

/** Derive display score for proReport row when backend score field is null (e.g. ASES 0–100). */
export function resolveProReportScore(row: ProReportScoreRow): string {
  const scoreMax = row.scoreMax ?? undefined;

  if (row.score != null && !Number.isNaN(row.score)) {
    if (scoreMax != null) {
      return `${row.score}/${scoreMax}`;
    }
    if (row.proInstrument === ProInstrument.OHS || row.label?.toLowerCase().includes('oxford hip')) {
      return `${row.score}/48`;
    }
    if (row.proInstrument === ProInstrument.OKS || row.label?.toLowerCase().includes('oxford knee')) {
      return `${row.score}/48`;
    }
    if (row.proInstrument === ProInstrument.ASES || row.label?.toLowerCase().includes('ases')) {
      return `${row.score}/100`;
    }
    return String(row.score);
  }

  if (row.content?.match(/OHS score/i)) {
    const match = row.content.match(/OHS score\s+(\d+)\s*\/\s*48/i);
    if (match) {
      return `${match[1]}/48`;
    }
    return row.content;
  }

  if (row.content?.match(/OKS score/i)) {
    const match = row.content.match(/OKS score\s+(\d+)\s*\/\s*48/i);
    if (match) {
      return `${match[1]}/48`;
    }
    return row.content;
  }

  if (row.content?.match(/ASES score/i)) {
    const match = row.content.match(/ASES score\s+(\d+)\s*\/\s*100/i);
    if (match) {
      return `${match[1]}/100`;
    }
    return row.content;
  }

  const first = row.values?.[0];
  if (first?.startsWith('{')) {
    return '—';
  }

  if (first && /^\d+$/.test(first)) {
    const numeric = parseInt(first, 10);
    if (row.proInstrument === ProInstrument.OHS || row.label?.toLowerCase().includes('oxford hip')) {
      if (numeric >= 0 && numeric <= 48) {
        return `${numeric}/48`;
      }
    }
    if (row.proInstrument === ProInstrument.OKS || row.label?.toLowerCase().includes('oxford knee')) {
      if (numeric >= 0 && numeric <= 48) {
        return `${numeric}/48`;
      }
    }
    if (numeric > 10 && numeric <= 100) {
      return `${numeric}/100`;
    }
    if (numeric >= 0 && numeric <= 10) {
      return String(numeric);
    }
  }

  if (row.label?.toLowerCase().includes('oxford hip')) {
    return '—';
  }

  if (row.label?.toLowerCase().includes('oxford knee')) {
    return '—';
  }

  if (row.label?.toLowerCase().includes('ases')) {
    return '—';
  }

  return '—';
}
